<template>
  <div class="attachments-list">
    <LoadingIndicator v-if="loading" centered />
    <div v-if="!loading">
      <table v-if="attachments.length > 0" class="table">
        <thead>
          <tr>
            <th v-if="selectable" width="40">
              <b-form-checkbox v-model="selectAll" @input="toggleSelectAll" size="sm" class="my-0" />
            </th>
            <th></th>
            <th>Name</th>
            <th>Filename</th>
            <th>Size</th>
            <th>User</th>
            <th width="200">Created at</th>
            <th width="100">Actions</th>
          </tr>
        </thead>
        <Attachment
          v-for="item in attachments"
          :key="'ind' + item.id"
          :attachment="item"
          :isSelected="selectedAttachment && selectedAttachment.id === item.id"
          :selectable="selectable"
          :selected="selectedAttachments.includes(item.id)"
          :previewUrl="previewUrls[item.id] || null"
          @click="selectAttachment(item)"
          @select="(checked) => toggleAttachmentSelection(item.id, checked)"
          @delete="deleteAttachment(item)"
          @download="fetchAttachment(item)"
        />
      </table>
      <div v-else class="alert alert-info">
        No attachments have been added yet.
      </div>
    </div>
    <h5 class="mt-5 mb-3">Add New Attachment</h5>
    <AttachmentForm ref="attachmentForm" :saving="saving" @submit="saveAttachment" />
  </div>
</template>

<script>
import { ApiClient } from '@/api/ApiClient';
import LoadingIndicator from '@/components/shared/LoadingIndicator';
import Attachment from '@/components/pages/carriers/Attachment.vue';
import AttachmentForm from '@/components/shared/AttachmentForm.vue';

export default {
  props: {
    entityId: {
      type: [Number, String],
      required: true,
    },
    entityType: {
      type: String,
      default: 'lead',
    },
    selectable: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    LoadingIndicator,
    Attachment,
    AttachmentForm,
  },
  data() {
    return {
      attachments: [],
      loading: false,
      saving: false,
      deleting: false,
      attachmentConut: 0,
      selectedAttachments: [],
      selectedAttachment: null,
      previewUrls: {},
      selectAll: false,
    };
  },
  computed: {
    attachmentsApi() {
      if (this.entityType === 'carrier') {
        return {
          get: (params) => ApiClient.carriers.attachments.get({ carrierId: this.entityId, ...params }),
          post: (params) => ApiClient.carriers.attachments.post({ carrierId: this.entityId, ...params }),
          delete: (params) => ApiClient.carriers.attachments.delete({ carrierId: this.entityId, ...params }),
        };
      }
      return {
        get: (params) => ApiClient.leads.attachments.get({ leadId: this.entityId, ...params }),
        post: (params) => ApiClient.leads.attachments.post({ leadId: this.entityId, ...params }),
        delete: (params) => ApiClient.leads.attachments.delete({ leadId: this.entityId, ...params }),
      };
    },
  },
  methods: {
    async selectAttachment(attachment) {
      if (this.selectedAttachment && this.selectedAttachment.id === attachment.id) {
        this.selectedAttachment = null;
        return;
      }
      this.selectedAttachment = attachment;
      if (!this.previewUrls[attachment.id]) {
        const response = await this.attachmentsApi.get({ id: attachment.id, blobRespone: true });
        const contentType = response.headers['content-type'] || 'application/octet-stream';
        this.$set(this.previewUrls, attachment.id, window.URL.createObjectURL(new Blob([response.data], { type: contentType })));
      }
    },
    toggleAttachmentSelection(id, checked) {
      if (checked) {
        if (!this.selectedAttachments.includes(id)) this.selectedAttachments.push(id);
      } else {
        this.selectedAttachments = this.selectedAttachments.filter(x => x !== id);
      }
    },
    toggleSelectAll(checked) {
      this.selectedAttachments = checked ? this.attachments.map(a => a.id) : [];
    },
    async saveAttachment({ name, file }) {
      this.saving = true;

      const data = new FormData();
      data.append('file', file, file.name);
      if (name) data.append('name', name);

      const response = await this.attachmentsApi.post({ body: data });
      this.saving = false;
      if (response.status === 200) {
        this.attachments.push(response.data.data);
        this.$refs.attachmentForm.reset();
      }
      this.selectedAttachments = [];
      if (this.entityType === 'lead') this.getAttachCount();
    },
    async deleteAttachment(attachment) {
      this.deleting = true;
      await this.attachmentsApi.delete({ id: attachment.id });
      this.deleting = false;
      this.attachments = this.attachments.filter(x => x.id != attachment.id);
      this.selectedAttachments = [];
      if (this.entityType === 'lead') this.getAttachCount();
    },
    async fetchAttachment(attachment) {
      let response = await this.attachmentsApi.get({ id: attachment.id, blobRespone: true });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename);
      link.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 0);
    },
    getAttachCount() {
      this.$parent.$parent.reloadLeadlikeData();
    },
  },
  watch: {
    selectedAttachments(val) {
      this.$emit('update:selectedAttachments', val);
      this.selectAll = val.length === this.attachments.length && this.attachments.length > 0;
    },
    entityId: {
      immediate: true,
      async handler() {
        this.loading = true;
        const response = await this.attachmentsApi.get({});
        this.loading = false;
        this.attachments = response.data.data;
        this.$emit('getcount', Object.keys(this.attachments).length);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.action {
  font-size: 16px;
}
</style>
